"use client";

import Image from "next/image";
import Link from "@/components/Link";
import { AppRoutes } from "@/lib/routeResolver";
import { slugifySegment, normalizeAbsoluteUrl } from "@/lib/urlUtils";
import type { CurvySliderItem } from "@/lib/navigation";

const FALLBACK_PHOTO = "/assets/img/placeholder.jpg";

function hasValue(v: string | null | undefined): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * Resolves an internal route based on which entity ids are populated.
 * Precedence (most specific first):
 *   package > tour > cruiseType > destination > region > pageContainer > catch-all
 * The `url` field holds the slug for the resolved entity.
 */
function resolveInternalSliderRoute(item: CurvySliderItem): string {
  const slug = slugifySegment(item.url || item.title || "");
  if (!slug) return AppRoutes.home();

  if (hasValue(item.packageId)) return AppRoutes.package(slug);
  if (hasValue(item.tourId)) return AppRoutes.experienceTour(slug);
  // Per-cruise-type listing pages were retired in favour of region/destination
  // cruise pages; cruise-type slides now land on the main cruises listing.
  if (hasValue(item.cruiseTypeId)) return AppRoutes.cruises();
  if (hasValue(item.destinationId)) return AppRoutes.destination(slug);
  if (hasValue(item.regionId)) return AppRoutes.region(slug);
  if (hasValue(item.pageContainerId)) return AppRoutes.dynamicPage(slug);

  return AppRoutes.dynamicPage(slug);
}

function buildSliderHref(item: CurvySliderItem): string {
  if (item.isAbsoluteUrl) return normalizeAbsoluteUrl(item.url ?? "");
  return resolveInternalSliderRoute(item);
}

interface SliderLinkProps {
  href: string;
  external: boolean;
  target: string;
  className?: string;
  title?: string;
  children: React.ReactNode;
}

function SliderLink({
  href,
  external,
  target,
  className,
  title,
  children,
}: SliderLinkProps) {
  if (external) {
    return (
      <a
        href={href}
        target={target}
        // `rel` is only needed when we open a new browsing context.
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        className={className}
        title={title}
      >
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} title={title}>
      {children}
    </Link>
  );
}

export default function CurvySliderSlide({ item }: { item: CurvySliderItem }) {
  if (!item) return null;

  const href = buildSliderHref(item);
  const external = Boolean(item.isAbsoluteUrl);
  // Absolute links honor the CMS `target`: null → new tab (`_blank`); a set
  // value (e.g. `_self`) is used verbatim. Internal links always use `<Link>`.
  const target = hasValue(item.target) ? item.target.trim() : "_blank";
  const title = item.title?.trim() || "";
  const photo = hasValue(item.photo) ? item.photo : FALLBACK_PHOTO;

  return (
    <div className="category-card single">
      <div className="box-img global-img">
        <SliderLink
          href={href}
          external={external}
          target={target}
          title={title || "View more"}
        >
          <Image
            src={photo}
            alt={title || "slide"}
            width={312}
            height={312}
            style={{ width: "100%", height: 250 }}
            unoptimized
          />
        </SliderLink>
      </div>

      {title && (
        <h3 className="box-title">
          <SliderLink
            href={href}
            external={external}
            target={target}
            title={title}
          >
            {title}
          </SliderLink>
        </h3>
      )}

      <SliderLink
        href={href}
        external={external}
        target={target}
        className="line-btn"
        title={title || "View more"}
      >
        See more
        {title && <span className="visually-hidden">{` about ${title}`}</span>}
      </SliderLink>
    </div>
  );
}
